/**
 * Programmatic listing-page SEO copy assembler.
 *
 * Ownership: City = market · Venue = operations · Listing = transaction.
 * Listings restate event facts in prose and link to authority hubs.
 * They must NOT embed venue-guide or city-hub section bodies, and must NOT
 * invent bios or tourism copy. Enrichment is listing-safe facts only.
 *
 * Stable enrichment contract (filled by GET /api/seo-discovery/listing-enrichment):
 * {
 *   venue: { path, eventsPath, venueName, capacity, venueType, areaName, address, timezone, subpages } | null,
 *   city: { path, city, state, slug, venueCount, eventCount } | null,
 *   performers: [{ name, path, eventsPath, genres? }],
 *   relationships: { performers, venue, city, category, venueSubpages, edges }
 * }
 *
 * @see LISTING_SEO.md
 */

import {
  formatListingEventDateTime,
  listingEventDateInfo,
} from './listingEventDate';

const stripHtml = value =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clean = value => {
  const s = stripHtml(value);
  return s || null;
};

const truncate = (text, max) => {
  const s = String(text || '').trim();
  if (!s || s.length <= max) {
    return s;
  }
  const sliced = s.slice(0, max - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  return `${(lastSpace > 40 ? sliced.slice(0, lastSpace) : sliced).trim()}…`;
};

const US_STATE_NAMES = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

/** Map listing category / browse labels to a natural event-type phrase. */
export const resolveEventTypeLabel = (category, { genres, eventLabel, venueName } = {}) => {
  const raw = clean(category);
  const lower = (raw || '').toLowerCase();
  const genre = Array.isArray(genres) && genres.length ? clean(genres[0]) : null;
  const theaterKind = resolveTheaterKind({ category: raw, eventLabel, venueName, genres });

  if (theaterKind === 'broadway') {
    return 'Broadway musical';
  }
  if (theaterKind === 'theater') {
    return 'theatrical show';
  }

  if (!lower) {
    return genre ? `${genre} concert` : 'live event';
  }

  if (/\bcomedy\b/.test(lower)) {
    return 'comedy show';
  }
  if (/\bsport/.test(lower)) {
    return genre || 'sports event';
  }
  if (/\b(theater|theatre|broadway|musical)\b/.test(lower)) {
    return 'theatrical show';
  }
  // Music/concert only when theater signals did not win (TN often tags musicals as music).
  if (/\bconcerts?\b/.test(lower) || /\bmusic\b/.test(lower) || lower === 'music') {
    return genre ? `${genre} concert` : 'concert';
  }
  if (/\bfestival\b/.test(lower)) {
    return 'festival';
  }
  if (/\bfamily\b/.test(lower)) {
    return 'family event';
  }
  if (/\bperforming\b/.test(lower)) {
    return 'performing arts event';
  }

  if (lower.endsWith('s') && lower.length > 4 && !/\bnews\b/.test(lower)) {
    return lower.slice(0, -1);
  }
  return lower;
};

/**
 * Differentiate Broadway / theater from concerts when TN category is coarse ("music").
 * Uses category, event title, venue name, and a deterministic known-show list — no AI.
 * @returns {'broadway'|'theater'|null}
 */
export const resolveTheaterKind = ({ category, eventLabel, venueName, genres } = {}) => {
  const cat = clean(category) || '';
  const title = clean(eventLabel) || '';
  const venue = clean(venueName) || '';
  const genreBlob = Array.isArray(genres)
    ? genres.map(clean).filter(Boolean).join(' ')
    : clean(genres) || '';
  const haystack = `${title} ${venue} ${cat} ${genreBlob}`.toLowerCase();

  if (!haystack.trim()) {
    return null;
  }

  // Amphitheaters are concert venues even when the word "theater" appears.
  if (
    /\bamphi[\s-]*(theatre|theater)\b/i.test(venue) ||
    /\bamphi[\s-]*(theatre|theater)\b/i.test(haystack)
  ) {
    return null;
  }

  const categoryIsTheater = /\b(theater|theatre|broadway|musical|performing[\s-]?arts)\b/i.test(
    cat
  );
  const titleSignalsBroadway =
    /\b(broadway|off-broadway|west end)\b/i.test(title) ||
    /\bthe musical\b/i.test(title) ||
    /\b(stage musical|musical theatre|musical theater)\b/i.test(title);
  const knownMusical = BROADWAY_SHOW_HINT.test(title);
  const venueIsTheatrical =
    /\b(theatre|theater|playhouse|opera house)\b/i.test(venue) &&
    !/\b(movie|cinema|imax)\b/i.test(venue);

  // Known touring/Broadway musicals win even when TN tags the listing as "music".
  if (knownMusical || titleSignalsBroadway || /\bbroadway\b/i.test(cat)) {
    return 'broadway';
  }

  // Explicit theater category (not music) → theater / musical.
  if (categoryIsTheater) {
    if (/\bmusical\b/i.test(haystack)) {
      return 'broadway';
    }
    return 'theater';
  }

  // Theatrical venue alone is not enough — Beacon/Greek/Chicago Theatre host concerts.
  // Only combine venue + non-music title cues that suggest a stage production.
  if (
    venueIsTheatrical &&
    /\b(play|drama|opera|ballet|symphony|philharmonic|orchestra)\b/i.test(title)
  ) {
    return 'theater';
  }

  return null;
};

/**
 * Snapshot-friendly category label (title case; sports- and theater-aware).
 * TN category alone is often too coarse (musicals tagged as music) — venue/title override.
 */
export const resolveSnapshotCategoryLabel = (
  category,
  { genres, eventLabel, venueName } = {}
) => {
  const raw = clean(category);
  const lower = (raw || '').toLowerCase();
  const genre = Array.isArray(genres) && genres.length ? clean(genres[0]) : null;
  const haystack = `${eventLabel || ''} ${venueName || ''} ${genre || ''} ${raw || ''}`.toLowerCase();

  if (/\bnfl\b/.test(haystack) || looksLikeNflMatchup(haystack)) {
    return 'NFL Football';
  }
  if (/\bnba\b/.test(haystack) || looksLikeNbaMatchup(haystack)) {
    return 'NBA Basketball';
  }
  if (/\bmlb\b/.test(haystack) || /\bbaseball\b/.test(haystack) || looksLikeMlbMatchup(haystack)) {
    return 'MLB Baseball';
  }
  if (/\bnhl\b/.test(haystack) || /\bhockey\b/.test(haystack)) {
    return 'NHL Hockey';
  }
  if (/\bmls\b/.test(haystack) || /\bsoccer\b/.test(haystack)) {
    return 'Soccer';
  }
  if (/\bsport/.test(lower) || looksLikeSportsMatchup(haystack)) {
    return genre ? titleCaseWords(genre) : 'Sports';
  }

  const theaterKind = resolveTheaterKind({
    category: raw,
    eventLabel,
    venueName,
    genres,
  });
  if (theaterKind === 'broadway') {
    return 'Broadway Musical';
  }
  if (theaterKind === 'theater') {
    return 'Theater';
  }

  if (/\bcomedy\b/.test(lower) || /\bcomedy\b/.test(haystack)) {
    return 'Comedy';
  }
  if (/\bconcerts?\b/.test(lower) || /\bmusic\b/.test(lower) || lower === 'music') {
    return genre ? titleCaseWords(`${genre} Concert`) : 'Concert';
  }
  if (/\bfestival\b/.test(lower)) {
    return 'Festival';
  }

  const type = resolveEventTypeLabel(category, { genres, eventLabel, venueName });
  return titleCaseWords(type);
};

/** Sports matchup titles (vs) that are not theater/music false positives. */
const looksLikeSportsMatchup = haystack =>
  /\bvs\.?\b/i.test(haystack) &&
  (NFL_TEAM_HINT.test(haystack) ||
    NBA_TEAM_HINT.test(haystack) ||
    MLB_TEAM_HINT.test(haystack) ||
    /\b(united|fc|cf|soccer|football|basketball|baseball|hockey|mlb|nfl|nba|nhl)\b/i.test(
      haystack
    ));

/**
 * Major Broadway / touring musicals often miscategorized as "music" by TicketNetwork.
 * Deterministic allowlist — extend as needed; Performer SEO can refine later.
 */
const BROADWAY_SHOW_HINT =
  /\b(hamilton|wicked|the lion king|lion king|chicago|the phantom of the opera|phantom of the opera|moulin rouge|hadestown|six the musical|& juliet|beetlejuice|back to the future|the book of mormon|book of mormon|dear evan hansen|come from away|aladdin|frozen|mean girls|harry potter and the cursed child|les mis[eé]rables|miss saigon|\brent\b|mamma mia|jersey boys|sweeney todd|into the woods|west side story|my fair lady|hello.?dolly|the music man|funny girl|\bcabaret\b|mj the musical|the notebook|water for elephants|outsiders|hell'?s kitchen|stereophonic|maybe happy ending)\b/i;

const NFL_TEAM_HINT =
  /\b(dolphins|chiefs|patriots|bills|jets|steelers|ravens|bengals|browns|titans|colts|jaguars|texans|broncos|raiders|chargers|cowboys|giants|eagles|commanders|packers|bears|lions|vikings|falcons|saints|panthers|buccaneers|bucs|49ers|seahawks|rams|cardinals)\b/i;

const NBA_TEAM_HINT =
  /\b(lakers|celtics|warriors|knicks|nets|bulls|heat|spurs|mavs|mavericks|nuggets|suns|bucks|sixers|76ers|raptors|thunder|timberwolves|pelicans|hawks|hornets|wizards|pistons|pacers|cavaliers|cavs|grizzlies|rockets|jazz|blazers|clippers|kings|magic)\b/i;

const MLB_TEAM_HINT =
  /\b(yankees|red sox|mets|phillies|braves|nationals|marlins|orioles|blue jays|rays|white sox|guardians|tigers|twins|royals|astros|rangers|athletics|a'?s|angels|mariners|dodgers|giants|padres|diamondbacks|rockies|cubs|cardinals|brewers|pirates|reds)\b/i;

const looksLikeNflMatchup = haystack =>
  /\bvs\.?\b/.test(haystack) && NFL_TEAM_HINT.test(haystack);

const looksLikeNbaMatchup = haystack =>
  /\bvs\.?\b/.test(haystack) && NBA_TEAM_HINT.test(haystack);

const looksLikeMlbMatchup = haystack =>
  /\bvs\.?\b/.test(haystack) && MLB_TEAM_HINT.test(haystack);

const titleCaseWords = value =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

export const expandStateName = state => {
  const raw = clean(state);
  if (!raw) {
    return null;
  }
  if (raw.length === 2) {
    return US_STATE_NAMES[raw.toUpperCase()] || raw;
  }
  return raw;
};

export const performerNamesFromPublicData = publicData => {
  const pd = publicData || {};
  const raw =
    clean(pd.headlinersAndArtists) ||
    clean(pd.performerName) ||
    clean(pd.performers) ||
    null;
  if (!raw) {
    return [];
  }
  return raw
    .split(/\s*(?:,|&|\/|\band\b)\s*/i)
    .map(p => clean(p))
    .filter(Boolean);
};

/**
 * Split sports matchup titles: "Miami Dolphins vs. Kansas City Chiefs".
 * Returns null when not a clear two-sided matchup.
 */
export const parseSportsMatchup = (eventLabel, performers = []) => {
  const label = clean(eventLabel) || '';
  const vsMatch = label.match(/^(.+?)\s+vs\.?\s+(.+)$/i);
  if (vsMatch) {
    const home = clean(vsMatch[1]);
    const visiting = clean(vsMatch[2]);
    if (home && visiting) {
      return { home, visiting };
    }
  }
  if (performers.length === 2 && /\bsport/i.test(String(eventLabel || ''))) {
    return { home: performers[0], visiting: performers[1] };
  }
  return null;
};

/** Strip a trailing "Tickets" token so callers can re-append exactly once. */
export const stripTrailingTickets = value =>
  String(clean(value) || '')
    .replace(/\s+tickets\s*$/i, '')
    .trim();

/** Append "Tickets" without duplicating an existing trailing Tickets token. */
export const withTicketsSuffix = value => {
  const base = stripTrailingTickets(value) || 'Event';
  return `${base} Tickets`;
};

/**
 * Material event differentiators that must not be truncated solely to hit the
 * soft ~65 character title target.
 */
export const hasMaterialEventQualifier = value => {
  const s = clean(value) || '';
  if (!s) {
    return false;
  }
  if (/\([^)]+\)/.test(s)) {
    return true;
  }
  return /\b(tribute(?:\s+experience)?|experience|festival|tour|post[\s-]?game(?:\s+concert)?|comedy|rodeo|exhibition|semi-?finals?|quarter-?finals?|finals?)\b/i.test(
    s
  );
};

/**
 * SEO-only cleanup of one matchup side. Conservative: colon-prefix stripping is
 * accepted only when the shortened result is clearly usable as a participant name.
 * Does not mutate authoritative event titles or imply app-wide colon rules.
 */
export const cleanMatchupSideForSeo = (side, { role = 'home' } = {}) => {
  const original = clean(side);
  if (!original) {
    return null;
  }

  let cleaned = original;

  if (role === 'visiting') {
    cleaned = cleaned.replace(/\s*[-–—]\s*Match\s+\d+\s*$/i, '').trim() || original;
  }

  if (role === 'home' && cleaned.includes(':')) {
    const afterColon = cleaned.slice(cleaned.lastIndexOf(':') + 1).trim();
    const usableParticipant =
      afterColon &&
      afterColon.length >= 2 &&
      !/^\d+$/.test(afterColon) &&
      !/^(group|match|round|stage|leg|game)\b/i.test(afterColon) &&
      !/[:/]/.test(afterColon) &&
      (/^[A-Z0-9]/.test(afterColon) || afterColon.length >= 3);
    cleaned = usableParticipant ? afterColon : original;
  }

  return cleaned || original;
};

/**
 * SEO-focused primary event label for title / H1 / meta / intro.
 * Does not replace authoritative eventLabel / eventEntityName used for schema.
 */
export const deriveSeoPrimaryEventLabel = ({ eventLabel, matchup } = {}) => {
  const formal = clean(eventLabel) || 'Event';
  if (!matchup?.home || !matchup?.visiting) {
    return formal;
  }

  const home = cleanMatchupSideForSeo(matchup.home, { role: 'home' });
  const visiting = cleanMatchupSideForSeo(matchup.visiting, { role: 'visiting' });
  if (!home || !visiting) {
    return formal;
  }

  // Reject ambiguous cleanup that still looks like tournament scaffolding.
  if (
    /^(group|match|round|stage)\b/i.test(home) ||
    /^(group|match|round|stage)\b/i.test(visiting)
  ) {
    return formal;
  }

  return `${home} vs. ${visiting}`;
};

export const venueNameFromPublicData = publicData => {
  const pd = publicData || {};
  return clean(pd.venueName) || clean(pd.venue) || clean(pd.location?.name) || null;
};

export const cityStateFromPublicData = publicData => {
  const pd = publicData || {};
  const loc = pd.location || {};
  const city = clean(pd.venueCity) || clean(loc.city) || clean(loc.addressLocality);
  const state = clean(pd.venueState) || clean(loc.state) || clean(loc.addressRegion);
  return { city, state };
};

export const streetAddressFromPublicData = publicData => {
  const pd = publicData || {};
  const loc = pd.location || {};
  return (
    clean(pd.venueStreetAddress) ||
    clean(loc.street) ||
    clean(loc.address) ||
    clean(loc.streetAddress) ||
    null
  );
};

/**
 * Natural city phrase for prose (avoid "New York, New York").
 */
export const cityPhraseForCopy = ({ city, state } = {}) => {
  if (!city) {
    return null;
  }
  if (/^new york$/i.test(city) && (!state || /^(NY|New York)$/i.test(state))) {
    return 'New York City';
  }
  if (/^washington$/i.test(city) && (!state || /^(DC|D\.C\.)$/i.test(state))) {
    return 'Washington, D.C.';
  }
  return city;
};

/**
 * Display city with full state name when possible: "Miami Gardens, Florida".
 */
export const cityLabelForDisplay = ({ city, state } = {}) => {
  if (!city) {
    return null;
  }
  const phrase = cityPhraseForCopy({ city, state });
  if (/^new york city$/i.test(phrase) || /^washington, d\.c\.$/i.test(phrase)) {
    return phrase;
  }
  const stateFull = expandStateName(state);
  return stateFull ? `${phrase}, ${stateFull}` : phrase;
};

/**
 * Event entity name for schema.org Event.name (not the SEO title).
 */
export const resolveEventEntityName = ({ title, publicData } = {}) => {
  const pd = publicData || {};
  const fromEvent = clean(pd.eventName);
  if (fromEvent) {
    return fromEvent;
  }
  const performers = performerNamesFromPublicData(pd);
  if (performers.length === 1) {
    return performers[0];
  }
  if (performers.length > 1) {
    return performers.join(', ');
  }
  const fromPerformer = clean(pd.performerName);
  if (fromPerformer) {
    return fromPerformer;
  }
  const rawTitle = clean(title);
  if (!rawTitle) {
    return 'Event';
  }
  return (
    rawTitle
      .replace(/\s*[—–-]\s*.*\btickets\b.*$/i, '')
      .replace(/\s+\btickets\b.*$/i, '')
      .trim() || rawTitle
  );
};

const cityDisplay = ({ city, state }) => {
  if (!city) {
    return null;
  }
  return state ? `${city}, ${state}` : city;
};

const shortDateForTitle = publicData => {
  const info = listingEventDateInfo({ publicData });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.date.format('MMM D, YYYY');
};

const shortDateCompact = publicData => {
  const info = listingEventDateInfo({ publicData });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.date.format('MMM D');
};

const yearFromPublicData = publicData => {
  const info = listingEventDateInfo({ publicData });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return String(info.date.year());
};

const formatHeroDate = ({ publicData, description } = {}) => {
  const info = listingEventDateInfo({ publicData, description });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.date.format('dddd, MMMM D, YYYY');
};

const formatHeroTime = ({ publicData, description } = {}) => {
  const info = listingEventDateInfo({ publicData, description });
  if (!info?.date?.isValid?.() || !info.hasTime) {
    return null;
  }
  return info.date.format('h:mm A');
};

const formatIntroDateTime = ({ publicData, description } = {}) => {
  const info = listingEventDateInfo({ publicData, description });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.hasTime
    ? info.date.format('dddd, MMMM D, YYYY [at] h:mm A')
    : info.date.format('dddd, MMMM D, YYYY');
};

/** Meta description date — complete phrase, no weekday (keeps the blurb short). */
const formatMetaDateTime = ({ publicData, description } = {}) => {
  const info = listingEventDateInfo({ publicData, description });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.hasTime
    ? info.date.format('MMMM D, YYYY [at] h:mm A')
    : info.date.format('MMMM D, YYYY');
};

const formatSnapshotStart = ({ publicData, description } = {}) => {
  const info = listingEventDateInfo({ publicData, description });
  if (!info?.date?.isValid?.()) {
    return null;
  }
  return info.hasTime
    ? info.date.format('MMMM D, YYYY [•] h:mm A')
    : info.date.format('MMMM D, YYYY');
};

/**
 * Document title soft target ~65 characters (not a hard Google limit).
 * Prefer event + city + date. Venue is intentionally omitted from the title chain.
 * When over target: drop city first, then date, before touching event identity.
 * Material qualifiers must never be truncated solely to satisfy maxLen.
 */
export const buildListingDocumentTitle = ({
  eventLabel,
  dateCompact,
  dateShort,
  city,
  maxLen = 65,
} = {}) => {
  const label = stripTrailingTickets(eventLabel) || 'Event';
  const ticketsBase = withTicketsSuffix(label);
  const cityName = clean(city);
  const date = clean(dateShort) || clean(dateCompact);

  // Preferred complete form first; when shortening, drop city before date.
  const candidates = [
    cityName && date ? `${ticketsBase} – ${cityName} – ${date}` : null,
    date ? `${ticketsBase} – ${date}` : null,
    cityName ? `${ticketsBase} – ${cityName}` : null,
    ticketsBase,
    label,
  ].filter(Boolean);

  const fitting = candidates.find(c => c.length <= maxLen);
  if (fitting) {
    return fitting;
  }

  // Never create a misleading title by truncating material event identity.
  if (hasMaterialEventQualifier(label) || hasMaterialEventQualifier(ticketsBase)) {
    return ticketsBase;
  }

  return truncate(ticketsBase, maxLen);
};

const joinNaturalList = names => {
  const list = (names || []).filter(Boolean);
  if (!list.length) {
    return null;
  }
  if (list.length === 1) {
    return list[0];
  }
  if (list.length === 2) {
    return `${list[0]} and ${list[1]}`;
  }
  return `${list.slice(0, -1).join(', ')}, and ${list[list.length - 1]}`;
};

const buildPerformerEntities = ({ performers, enrichmentPerformers }) => {
  const names = Array.isArray(performers) ? performers : [];
  if (!names.length) {
    return [];
  }
  const enriched = Array.isArray(enrichmentPerformers) ? enrichmentPerformers : [];
  const byName = new Map(
    enriched.map(p => [String(p?.name || '').trim().toLowerCase(), p])
  );

  return names.map(name => {
    const match = byName.get(String(name).toLowerCase());
    return {
      name,
      path: match?.path || null,
      eventsPath: match?.eventsPath || null,
      performerId: match?.performerId || null,
    };
  });
};

/**
 * First visible SEO paragraph — ticket intent first (no parking / nearby filler).
 */
const buildIntroParagraph = ({
  seoPrimaryEventLabel,
  venue,
  cityPhrase,
  cityLabel,
  introDate,
  matchup,
}) => {
  const eventName = clean(seoPrimaryEventLabel);
  if (!eventName) {
    return null;
  }

  const inCity = cityPhrase || cityLabel;
  const when = introDate || null;
  const matchupHome = matchup?.home
    ? cleanMatchupSideForSeo(matchup.home, { role: 'home' })
    : null;
  const matchupVisiting = matchup?.visiting
    ? cleanMatchupSideForSeo(matchup.visiting, { role: 'visiting' })
    : null;

  const appendWhereWhen = parts => {
    let text = parts;
    if (when) {
      text += ` on ${when}`;
    }
    if (venue) {
      text += ` at ${venue}`;
    }
    if (inCity) {
      text += ` in ${inCity}`;
    }
    return text;
  };

  if (matchupHome && matchupVisiting && venue) {
    return `${appendWhereWhen(
      `Find tickets for ${matchupHome} vs. ${matchupVisiting}`
    )}. Compare current ticket options, seating sections, and prices below to find the seats that work best for you.`;
  }

  if (venue) {
    const lead = when
      ? `Find ${eventName} tickets for ${when} at ${venue}`
      : `Find ${eventName} tickets at ${venue}`;
    const withCity = inCity ? `${lead} in ${inCity}` : lead;
    return `${withCity}. Compare current ticket options, seating sections, and prices below to find the seats that work best for you.`;
  }

  let lead = `Find ${eventName} tickets`;
  if (when) {
    lead += ` for ${when}`;
  }
  if (inCity) {
    lead += ` in ${inCity}`;
  }
  return `${lead}. Compare current ticket options, seating sections, and prices below.`;
};

/**
 * Purchasing-intent meta description. Complete sentences; no inventory claims.
 */
const buildMetaDescription = ({
  seoPrimaryEventLabel,
  venue,
  cityPhrase,
  cityLabel,
  metaDate,
  marketplaceName = 'PromoTix',
}) => {
  const eventName = stripTrailingTickets(seoPrimaryEventLabel) || 'Event';
  const inCity = cityPhrase || cityLabel || null;
  const market = clean(marketplaceName) || 'PromoTix';
  const cta = `Compare ticket options and current prices, then check out securely on ${market}.`;

  let lead = `Find ${eventName} tickets`;
  if (metaDate && venue && inCity) {
    lead += ` for ${metaDate} at ${venue} in ${inCity}`;
  } else if (metaDate && venue) {
    lead += ` for ${metaDate} at ${venue}`;
  } else if (metaDate && inCity) {
    lead += ` for ${metaDate} in ${inCity}`;
  } else if (venue && inCity) {
    lead += ` at ${venue} in ${inCity}`;
  } else if (metaDate) {
    lead += ` for ${metaDate}`;
  } else if (venue) {
    lead += ` at ${venue}`;
  } else if (inCity) {
    lead += ` in ${inCity}`;
  }

  return `${lead}. ${cta}`;
};

const buildAboutParagraph = ({
  eventLabel,
  venue,
  cityPhrase,
  cityLabel,
  matchup,
  performers,
  eventType,
  snapshotCategory,
}) => {
  const inCity = cityPhrase || cityLabel;
  const categoryLabel = snapshotCategory || eventType;

  if (matchup?.home && matchup?.visiting && venue) {
    let text = `This matchup features the ${matchup.home} hosting the ${matchup.visiting} at ${venue}.`;
    if (inCity && categoryLabel) {
      text += ` Fans looking for upcoming ${categoryLabel.toLowerCase()} in ${inCity} can also browse additional ${matchup.home} home games and other sporting events nearby.`;
    } else if (inCity) {
      text += ` Fans can also browse additional ${matchup.home} games and other events nearby in ${inCity}.`;
    } else {
      text += ` Fans can also browse additional ${matchup.home} games and related events.`;
    }
    return text;
  }

  const artistList = joinNaturalList(performers);
  if (artistList && venue) {
    let text = `${artistList} ${performers.length > 1 ? 'perform' : 'performs'} at ${venue}`;
    if (inCity) {
      text += ` in ${inCity}`;
    }
    text += '.';
    if (performers.length === 1) {
      text += ` Browse more upcoming dates and related ${categoryLabel || 'live'} events nearby.`;
    } else {
      text += ` Compare tickets for this date and explore more shows at the venue or nearby.`;
    }
    return text;
  }

  if (eventLabel && venue) {
    return `${eventLabel} is scheduled at ${venue}${
      inCity ? ` in ${inCity}` : ''
    }. Compare seats for this performance and explore more events at the venue.`;
  }

  return null;
};

const buildEventSnapshotRows = ({
  snapshotCategory,
  matchup,
  performers,
  venue,
  cityDisplayLabel,
  snapshotStart,
  ticketType,
  isSports,
}) => {
  const rows = [];

  if (snapshotCategory) {
    rows.push({
      label: 'Event Category',
      value: snapshotCategory,
      linkPath: null,
      linkKind: null,
    });
  }

  if (matchup?.home) {
    rows.push({
      label: 'Home Team',
      value: matchup.home,
      linkPath: null,
      linkKind: 'performer',
      entityName: matchup.home,
    });
    rows.push({
      label: 'Visiting Team',
      value: matchup.visiting,
      linkPath: null,
      linkKind: 'performer',
      entityName: matchup.visiting,
    });
  } else if (performers.length === 1) {
    rows.push({
      label: 'Performer',
      value: performers[0].name,
      linkPath: performers[0].path,
      linkKind: 'performer',
      entityName: performers[0].name,
    });
  } else if (performers.length > 1 && performers.length <= 4) {
    rows.push({
      label: 'Performers',
      value: performers.map(p => p.name).join(', '),
      linkPath: null,
      linkKind: null,
    });
  }

  if (venue) {
    rows.push({
      label: 'Venue',
      value: venue,
      linkPath: null,
      linkKind: 'venue',
    });
  }

  if (cityDisplayLabel) {
    rows.push({
      label: 'City',
      value: cityDisplayLabel,
      linkPath: null,
      linkKind: 'city',
    });
  }

  if (snapshotStart) {
    rows.push({
      label: 'Start Time',
      value: snapshotStart,
      linkPath: null,
      linkKind: null,
    });
  }

  if (ticketType) {
    rows.push({
      label: 'Ticket Type',
      value: ticketType,
      linkPath: null,
      linkKind: null,
    });
  }

  return rows;
};

const attachSnapshotLinks = (rows, { performers, venuePath, cityPath }) => {
  const byPerformer = new Map(
    (performers || []).map(p => [String(p.name).toLowerCase(), p.path || null])
  );

  return (rows || []).map(row => {
    if (row.linkKind === 'performer' && row.entityName) {
      return {
        ...row,
        linkPath: byPerformer.get(String(row.entityName).toLowerCase()) || row.linkPath,
      };
    }
    if (row.linkKind === 'venue') {
      return { ...row, linkPath: venuePath || null };
    }
    if (row.linkKind === 'city') {
      return { ...row, linkPath: cityPath || null };
    }
    return row;
  });
};

/**
 * @param {object} params
 * @param {string} [params.title]
 * @param {object} [params.publicData]
 * @param {string} [params.description] listing description (for Event date: lines)
 * @param {string} [params.marketplaceName]
 * @param {object} [params.enrichment] listing-safe enrichment (see file header)
 */
export const buildListingSeoCopy = ({
  title = '',
  publicData = {},
  description = '',
  marketplaceName = 'PromoTix',
  enrichment = null,
} = {}) => {
  const pd = publicData || {};
  const enrichmentSafe = enrichment && typeof enrichment === 'object' ? enrichment : {};

  let performers = performerNamesFromPublicData(pd);
  const eventEntityName = resolveEventEntityName({ title, publicData: pd });
  if (!performers.length && eventEntityName && eventEntityName !== 'Event') {
    // Avoid treating "Team A vs Team B" as a single performer name.
    if (!/\bvs\.?\b/i.test(eventEntityName)) {
      performers = [eventEntityName];
    }
  }

  const matchup = parseSportsMatchup(eventEntityName, performers);
  if (matchup) {
    performers = [matchup.home, matchup.visiting];
  }

  const primaryPerformer = performers[0] || null;
  const venue = venueNameFromPublicData(pd) || clean(enrichmentSafe.venue?.venueName);
  const { city, state } = cityStateFromPublicData(pd);
  const cityLabel = cityDisplay({ city, state });
  const cityPhrase = cityPhraseForCopy({ city, state });
  const cityDisplayLabel = cityLabelForDisplay({ city, state });
  const streetAddress = streetAddressFromPublicData(pd);
  const genres = Array.isArray(pd.musicGenreS)
    ? pd.musicGenreS.map(clean).filter(Boolean)
    : [];
  const category =
    clean(pd.category) ||
    clean(pd.eventCategory) ||
    clean(enrichmentSafe.category) ||
    null;
  const eventType = resolveEventTypeLabel(category, {
    genres,
    eventLabel: eventEntityName,
    venueName: venue,
  });
  const snapshotCategory = resolveSnapshotCategoryLabel(category, {
    genres,
    eventLabel: eventEntityName,
    venueName: venue,
  });
  const isSports =
    !!matchup || /\bsport/i.test(category || '') || /\b(nfl|nba|mlb|nhl|mls)\b/i.test(eventEntityName || '');
  const dateLong = formatListingEventDateTime({ publicData: pd, description });
  const dateShort = shortDateForTitle(pd);
  const dateCompact = shortDateCompact(pd);
  const introDate = formatIntroDateTime({ publicData: pd, description });
  const metaDate = formatMetaDateTime({ publicData: pd, description });
  const heroDate = formatHeroDate({ publicData: pd, description });
  const heroTime = formatHeroTime({ publicData: pd, description });
  const snapshotStart = formatSnapshotStart({ publicData: pd, description });
  const year = yearFromPublicData(pd);
  const eventLabel = eventEntityName;
  const ticketType = clean(pd.ticketType);

  // SEO-facing label may shorten sports matchups; authoritative eventLabel stays intact.
  const seoPrimaryEventLabel = deriveSeoPrimaryEventLabel({ eventLabel, matchup });

  // H1: matchups use SEO label; non-matchups keep full resolved event identity.
  const h1 = withTicketsSuffix(matchup ? seoPrimaryEventLabel : eventLabel);

  const introParagraph = buildIntroParagraph({
    seoPrimaryEventLabel,
    venue,
    cityPhrase,
    cityLabel: cityDisplayLabel || cityLabel,
    introDate,
    matchup,
  });

  const metaDescription = buildMetaDescription({
    seoPrimaryEventLabel,
    venue,
    cityPhrase,
    cityLabel: cityDisplayLabel || cityLabel,
    metaDate: metaDate || dateLong || year || null,
    marketplaceName,
  });

  const performerEntities = buildPerformerEntities({
    performers,
    enrichmentPerformers: enrichmentSafe.performers,
  });

  // Keep performerLineup for schema / older callers — no "Headliner" roles for on-page dump.
  const performerLineup = performerEntities.map(p => ({
    name: p.name,
    path: p.path,
    eventsPath: p.eventsPath,
    role: null,
  }));

  const aboutParagraph = buildAboutParagraph({
    eventLabel,
    venue,
    cityPhrase,
    cityLabel: cityDisplayLabel || cityLabel,
    matchup,
    performers: performers,
    eventType,
    snapshotCategory,
  });

  const aboutHeading = matchup
    ? 'About the teams'
    : performers.length > 1
      ? 'About the performers'
      : performers.length === 1
        ? 'About this event'
        : 'About this event';

  let snapshotRows = buildEventSnapshotRows({
    snapshotCategory,
    matchup,
    performers: performerEntities,
    venue,
    cityDisplayLabel: cityDisplayLabel || cityLabel,
    snapshotStart,
    ticketType,
    isSports,
  });
  snapshotRows = attachSnapshotLinks(snapshotRows, {
    performers: performerEntities,
    // Hub paths only — never keyword-search fallbacks (those 404 / are not entity hubs).
    venuePath: enrichmentSafe.venue?.path || null,
    cityPath: enrichmentSafe.city?.path || null,
  });

  const planningLinks = Array.isArray(enrichmentSafe.venue?.subpages)
    ? enrichmentSafe.venue.subpages
    : Array.isArray(enrichmentSafe.relationships?.venueSubpages)
      ? enrichmentSafe.relationships.venueSubpages
      : [];

  const documentTitle = buildListingDocumentTitle({
    eventLabel: seoPrimaryEventLabel,
    dateCompact,
    dateShort,
    city: cityPhrase || city,
    maxLen: 65,
  });

  const moreDatesSubject = matchup?.home || primaryPerformer || eventLabel;
  const primaryPerformerEntity =
    performerEntities.find(
      p => p.name.toLowerCase() === String(moreDatesSubject || '').toLowerCase()
    ) ||
    (performerEntities.length === 1 ? performerEntities[0] : null);

  // Keep formal event identity in breadcrumbs when SEO label was shortened.
  const breadcrumbEventName =
    seoPrimaryEventLabel !== eventLabel ? withTicketsSuffix(eventLabel) : h1;

  // City hub → Venue hub → Event/Performer hub (when launched). No keyword search URLs.
  // Unlaunched entities render as plain text (not links).
  const breadcrumbs = [
    cityDisplayLabel || cityLabel
      ? {
          name: cityDisplayLabel || cityLabel,
          path: enrichmentSafe.city?.path || null,
          kind: 'city',
        }
      : null,
    venue
      ? {
          name: venue,
          path: enrichmentSafe.venue?.path || null,
          kind: 'venue',
        }
      : null,
    {
      name: breadcrumbEventName,
      path: primaryPerformerEntity?.path || null,
      kind: primaryPerformerEntity?.path ? 'performer' : 'event',
    },
  ].filter(Boolean);

  // Nearby links: only real hub/spoke URLs. Omit an item when its destination is not live.
  const nearbyLinks = [
    moreDatesSubject &&
    (primaryPerformerEntity?.eventsPath || primaryPerformerEntity?.path)
      ? {
          key: 'performer-dates',
          label: `View more dates for ${moreDatesSubject}`,
          path: primaryPerformerEntity.eventsPath || primaryPerformerEntity.path,
          kind: 'performer',
        }
      : null,
    venue && enrichmentSafe.venue?.eventsPath
      ? {
          key: 'venue-events',
          label: `See upcoming events at ${venue}`,
          path: enrichmentSafe.venue.eventsPath,
          kind: 'venue',
        }
      : null,
    (cityDisplayLabel || cityLabel) && enrichmentSafe.city?.eventsPath
      ? {
          key: 'city-events',
          label: `Discover other events in ${cityDisplayLabel || cityLabel}`,
          path: enrichmentSafe.city.eventsPath,
          kind: 'city',
        }
      : null,
  ].filter(Boolean);

  return {
    h1,
    documentTitle,
    metaDescription,
    introParagraph,
    seoPrimaryEventLabel,
    /** @deprecated hero lines removed from UI — snapshot covers date/venue/city */
    hero: {
      date: heroDate,
      time: heroTime,
      venue,
      city: cityDisplayLabel || cityLabel,
    },
    eventSnapshot: {
      heading: 'Event Snapshot',
      rows: snapshotRows,
    },
    aboutHeading,
    aboutParagraph,
    planningHeading: 'Planning your visit?',
    planningLinks,
    nearbyLinks,
    explore: {
      venueHeading: venue ? `More events at ${venue}` : 'More events at this venue',
      venuePath: enrichmentSafe.venue?.eventsPath || enrichmentSafe.venue?.path || null,
      cityHeading:
        cityDisplayLabel || cityLabel
          ? `More events in ${cityDisplayLabel || cityLabel}`
          : 'More events in this city',
      cityPath: enrichmentSafe.city?.path || null,
      cityIntro:
        enrichmentSafe.city?.path && (cityDisplayLabel || cityLabel)
          ? `Browse the ${cityDisplayLabel || cityLabel} events hub for more shows nearby.`
          : null,
    },
    /** @deprecated kept for older callers — do not render as a lineup dump */
    venueSnippet: null,
    venueGuideCta: venue
      ? `${venue} seating, parking and visitor guide`
      : 'Venue seating, parking and visitor guide',
    citySnippet: null,
    performerLineup,
    /** @deprecated use performerLineup — kept for older callers */
    performerSnippets: performerLineup
      .filter(p => p.path)
      .map(p => ({
        name: p.name,
        path: p.path,
        text: `More about ${p.name}.`,
      })),
    eventEntityName,
    matchup,
    breadcrumbs,
    relationships: enrichmentSafe.relationships || {
      performers: performerLineup,
      venue: venue
        ? {
            name: venue,
            path: enrichmentSafe.venue?.path || null,
            eventsPath: enrichmentSafe.venue?.eventsPath || null,
          }
        : null,
      city: city
        ? {
            name: cityDisplayLabel || cityLabel,
            path: enrichmentSafe.city?.path || null,
          }
        : null,
      category,
      venueSubpages: planningLinks,
      edges: [],
    },
    fields: {
      performers,
      primaryPerformer,
      venue,
      city,
      state,
      cityLabel,
      cityPhrase,
      cityDisplayLabel,
      streetAddress,
      eventType,
      snapshotCategory,
      dateLong,
      dateShort,
      dateCompact,
      introDate,
      heroDate,
      heroTime,
      year,
      genres,
      category,
      eventLabel,
      seoPrimaryEventLabel,
      ticketType,
      isSports,
    },
    links: {
      venuePath: enrichmentSafe.venue?.path || null,
      venueEventsPath: enrichmentSafe.venue?.eventsPath || null,
      cityPath: enrichmentSafe.city?.path || null,
    },
  };
};

export const resolveListingPageDescription = ({
  title,
  description,
  publicData,
  marketplaceName,
  enrichment,
  isEvent,
}) => {
  if (!isEvent) {
    return clean(description) || '';
  }
  const seo = buildListingSeoCopy({
    title,
    publicData,
    description,
    marketplaceName,
    enrichment,
  });
  return seo.metaDescription || clean(description) || '';
};
